import { useCallback, useRef, useState } from 'react';

import { CardVariant } from '../knowledge-smart-card/knowledge-smart-card';
import type { ParsedElementType, ParsedStructuredElementType } from '../layout/types';

import { useDelayedResizeObserver } from './use-delayed-resize-observer';
import { getElementsForPosition } from './utils';

// Tags that represent text content
const TEXT_CONTENT_TAGS = ['md', 'list'];
// Tags that represent smart links
const SMART_LINK_TAGS = ['smart-link', 'action-card'];
// Max height of a full-size smart link card
const FULL_SMART_LINK_CARD_MAX_HEIGHT = 174;
// Height threshold for using compact card - based on full-size smart card height
export const COMPACT_SMART_LINK_CARD_THRESHOLD_HEIGHT = FULL_SMART_LINK_CARD_MAX_HEIGHT * 0.75;

/**
 * Check if an element is text content (markdown or list)
 */
const isTextContent = (element: ParsedElementType): boolean =>
	typeof element !== 'string' && TEXT_CONTENT_TAGS.includes(element.tag);

/**
 * Check if an element is a smart link
 */
const isSmartLink = (element: ParsedElementType): boolean =>
	typeof element !== 'string' && SMART_LINK_TAGS.includes(element.tag);

interface UseAdaptiveSmartCardVariantResult {
	/** The card variant to use based on measured text height */
	cardVariant: CardVariant;
	/** Whether the text content has been measured */
	isTextMeasured: boolean;
	/** Elements positioned in the left column */
	leftElements: ParsedStructuredElementType[];
	/** Whether the left column needs the measurement */
	leftNeedsMeasurement: boolean;
	/** Elements positioned in the right column */
	rightElements: ParsedStructuredElementType[];
	/** Whether the right column needs the measurement */
	rightNeedsMeasurement: boolean;
	/** Ref to attach to the text content container for height measurement */
	textContentRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to manage adaptive smart card variant selection based on text content height.
 *
 * This hook:
 * 1. Categorizes elements into text content (markdown, lists) and smart links
 * 2. Measures the text content height using ResizeObserver
 * 3. Determines the appropriate smart card variant (COMPACT vs FULL) based on text height
 *
 * @param elements - Array of parsed elements to categorize and measure
 * @returns Object containing refs, state, and helpers for adaptive rendering
 */
export function useAdaptiveSmartCardVariant(
	elements: ParsedElementType[],
): UseAdaptiveSmartCardVariantResult {
	const textContentRef = useRef<HTMLDivElement>(null);
	const [textHeight, setTextHeight] = useState<number | null>(null);

	// Measure text content height after render
	const handleResize = useCallback<ResizeObserverCallback>((entries) => {
		const entry = entries[0];
		if (entry) {
			setTextHeight(entry.contentRect.height);
		}
	}, []);

	useDelayedResizeObserver(textContentRef, handleResize);

	// Separate elements by position
	const leftElements = getElementsForPosition(elements, 'left', 0);
	const rightElements = getElementsForPosition(elements, 'right', 1);

	// Determine which column has text content and which has smart links
	const leftHasText = leftElements.some(isTextContent);
	const rightHasSmartLink = rightElements.some(isSmartLink);
	const leftHasSmartLink = leftElements.some(isSmartLink);
	const rightHasText = rightElements.some(isTextContent);

	// Determine card variant based on measured text height
	// Default to compact card and when text content is shorter than 75% of the full-size smart card height
	const isTextMeasured = textHeight !== null;
	const useCompactCard =
		!isTextMeasured || (isTextMeasured && textHeight <= COMPACT_SMART_LINK_CARD_THRESHOLD_HEIGHT);
	const cardVariant = useCompactCard ? CardVariant.COMPACT : CardVariant.FULL;

	// Determine which column should have the ref for measurement
	// Left column takes precedence when both columns would need measurement
	// (since only one ref can be attached at a time)
	const leftNeedsMeasurement = leftHasText && rightHasSmartLink;
	const rightNeedsMeasurement = rightHasText && leftHasSmartLink && !leftNeedsMeasurement;

	return {
		textContentRef,
		isTextMeasured,
		cardVariant,
		leftNeedsMeasurement,
		rightNeedsMeasurement,
		leftElements,
		rightElements,
	};
}
